import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { errorText } from '../../utils/errorText';
import { formatDelta } from '../../utils/thermal/legend';
import useLiveChannel from '../../hooks/useLiveChannel';
import { box } from '../shared/styles';
import ExperimentDesigner from './ExperimentDesigner';
import ExperimentDetail from './ExperimentDetail';

/*
 * Experiments (Task 6.5): design, run under a signed protocol version, enter
 * the real values, and -- only once complete -- analyse them, chart them with
 * their significance, export them, and ask for a draft interpretation that a
 * second person releases.
 *
 * Two views (phase C3, docs/FIELD_AI_VISION_SPEC.md section 43): Active
 * (planned and running), kept current every minute while shown, and History
 * (completed and abandoned), the archived ones only when asked for. Each row
 * says how many thermal captures its reader may see, when the newest was
 * taken, and the newest analysed one's differences from its reference.
 *
 * The experiment open is kept in the address (?experiment=, without a
 * navigation, as its detail keeps ?tab=), so the analysis workbench's Back
 * from a thermal capture opened in its Thermal tab comes to it again.
 */
export const REFRESH_MS = 60000;

/** The experiment the address names (?experiment=), else none. */
export function experimentFromAddress(search = (typeof window !== 'undefined' ? window.location.search : '')) {
  return new URLSearchParams(search).get('experiment') || null;
}

/** Keep ?experiment= in the address without a navigation; with none open, its tab goes too. */
function writeExperiment(id) {
  try {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('experiment', id);
    else {
      url.searchParams.delete('experiment');
      url.searchParams.delete('tab');
    }
    window.history.replaceState(window.history.state, '', url);
  } catch (_) { /* no address to keep it in */ }
}

function ThermalSummary({ thermal, t }) {
  if (!thermal) return null;
  const delta = (thermal.deltas || []).find((d) => d.delta_avg_c !== null);
  return (
    <small style={{ display: 'block', color: 'var(--c-text-muted-fg)' }} data-testid="experiment-thermal">
      {t('summary.captures', { count: thermal.captures })}
      {' · '}{t('summary.latest', { when: String(thermal.latest_at || '').slice(0, 10) })}
      {delta && thermal.reference_label && (
        <> · <bdi dir="ltr">{t('summary.delta', { label: delta.label, reference: thermal.reference_label,
          value: formatDelta(delta.delta_avg_c, 1) })}</bdi></>
      )}
    </small>
  );
}

export default function Experiments() {
  const { t } = useTranslation('experiments');
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [view, setView] = useState('active');
  const [archived, setArchived] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelectedState] = useState(() => experimentFromAddress());
  const setSelected = useCallback((id) => {
    setSelectedState(id);
    writeExperiment(id);
  }, []);
  const [designing, setDesigning] = useState(false);
  const [error, setError] = useState('');

  const params = { state: view, ...(view === 'history' && archived ? { archived: 1 } : {}),
    ...(query.trim() ? { q: query.trim() } : {}) };
  const key = JSON.stringify(params);
  const pull = useCallback(() => api.get('/experiments', { params: JSON.parse(key) }), [key]);
  const show = useCallback(({ data }) => { setList(data); setError(''); }, []);
  const fail = useCallback((e) => setError(errorText(e)), []);
  // Active: asked at once, then every minute while the page is shown.
  const poke = useLiveChannel(pull, show, { enabled: view === 'active', base: REFRESH_MS, max: 5 * REFRESH_MS,
    key, onError: fail });
  // History: asked once per view; a finished experiment does not change by itself.
  useEffect(() => {
    if (view !== 'history') return undefined;
    let live = true;
    const wait = setTimeout(() => pull().then((r) => live && show(r)).catch((e) => live && fail(e)), 0);
    return () => { live = false; clearTimeout(wait); };
  }, [view, pull, show, fail]);
  const load = useCallback(() => (view === 'active' ? poke() : pull().then(show).catch(fail)),
    [view, poke, pull, show, fail]);

  const create = async (body) => {
    const { data } = await api.post('/experiments', body);
    setDesigning(false);
    setView('active');
    await load();
    setSelected(data.experiment_id);
  };

  const tab = (name) => (
    <button type="button" aria-pressed={view === name} data-testid={`view-${name}`}
      onClick={() => setView(name)}
      style={{ background: 'none', border: 'none', padding: '4px 10px', cursor: 'pointer', fontSize: 14,
        color: 'var(--c-text-fg)', fontWeight: view === name ? 700 : 400,
        borderBlockEnd: `3px solid ${view === name ? 'var(--c-primary)' : 'transparent'}` }}>
      {t(`views.${name}`)}
    </button>
  );

  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('intro')}</p>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {can(user, 'experiment:write') && (
        <button type="button" data-testid="new-experiment" title={t('tip.newExperiment')} style={{ marginBottom: 12 }}
          onClick={() => { setDesigning(true); setSelected(null); }}>{t('newExperiment')}</button>
      )}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ ...box, minWidth: 240, flex: '0 1 300px', margin: 0 }}>
          <div role="group" aria-label={t('views.label')} style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {tab('active')}
            {tab('history')}
          </div>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('views.search')}
            aria-label={t('views.search')} data-testid="experiment-search"
            style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', marginBottom: 6 }} />
          {view === 'history' && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)}
                data-testid="show-archived" />
              {t('views.showArchived')}
            </label>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid={`experiment-list-${view}`}>
            {list.length === 0 && <li>{t(view === 'active' ? 'views.emptyActive' : 'views.emptyHistory')}</li>}
            {list.map((e) => (
              <li key={e.experiment_id}>
                <button type="button" data-testid={`experiment-${e.experiment_id}`} title={t('tip.openExperiment')}
                  onClick={() => { setSelected(e.experiment_id); setDesigning(false); }}
                  style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', textAlign: 'start',
                    color: 'var(--c-text-fg)', width: '100%' }}>
                  <strong>{e.code}</strong> {e.title}
                  <br />
                  <small style={{ color: 'var(--c-text-muted-fg)' }}>
                    {t(`status_${e.status}`)} · {t(`kind_${e.design_kind}`)}{e.archived_at ? ` · ${t('archived')}` : ''}
                  </small>
                  <ThermalSummary thermal={e.thermal} t={t} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ flex: '1 1 560px', minWidth: 0 }}>
          {designing && <ExperimentDesigner onSave={create} />}
          {!designing && selected && <ExperimentDetail key={selected} experimentId={selected} onChanged={load} />}
          {!designing && !selected && <p>{t('choose')}</p>}
        </div>
      </div>
    </div>
  );
}
